"use client";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Compass, ChevronDown } from "lucide-react";
import { useChatContext } from "../providers/ChatProvider";
import { neuTriggerLabelSx, neuTriggerSx } from "../motion";
import type { ModelCatalog, ModelDescriptor } from "../../../chat/client";

/**
 * Per-conversation model picker. Reads the allowlist from GET /v1/models,
 * stores the user's pick in ChatContext.selectedModel (default "auto"),
 * and lets the Composer decide whether to send a `model` field on the
 * chat request.
 *
 * "Auto" is the first option and the default. It maps to "don't send a
 * model field" so the backend's existing model_router (cost-driven) or
 * construction-time LLM_MODEL kicks in.
 *
 * Fail-silent: if /v1/models 401s / 5xx / network-errors, the picker
 * renders nothing — the composer keeps working with auto mode.
 */
export function ModelPicker() {
  const { client, selectedModel, setSelectedModel } = useChatContext();
  const [catalog, setCatalog] = useState<ModelCatalog | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const c = await client.listModels();
      if (!cancelled) setCatalog(c);
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  const current = useMemo<ModelDescriptor | null>(() => {
    if (!catalog) return null;
    return catalog.models.find((m) => m.id === selectedModel) ??
      catalog.models.find((m) => m.is_auto) ??
      catalog.models[0] ??
      null;
  }, [catalog, selectedModel]);

  if (!catalog || catalog.models.length <= 1) {
    // Either the backend returned nothing (auth/error) or there's only
    // Auto with no allow-listed alternatives — hide the picker entirely
    // rather than show a 1-item dropdown.
    return null;
  }

  return (
    <Box sx={{ display: "inline-flex" }}>
      <Tooltip
        title={
          catalog.router_enabled
            ? "Pick a model. Auto lets the backend choose per turn."
            : `Pick a model. Auto = the default (${catalog.default}).`
        }
        placement="top"
      >
        {/* Neumorphic dropdown trigger: a quiet soft pill that presses inward
            while the menu is open — matches the composer toolbar's soft-UI. */}
        <Button
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          startIcon={<Compass size={15} />}
          endIcon={<ChevronDown size={15} />}
          sx={neuTriggerSx(!!anchor)}
          aria-haspopup="true"
          aria-expanded={!!anchor}
        >
          <Box component="span" sx={neuTriggerLabelSx}>{current?.label ?? "Auto"}</Box>
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: -1, minWidth: 240 } } }}
      >
        {catalog.models.map((m) => (
          <MenuItem
            key={m.id}
            selected={m.id === selectedModel}
            onClick={() => {
              setSelectedModel(m.id);
              setAnchor(null);
            }}
          >
            <ListItemText
              primary={m.label}
              secondary={
                m.is_auto
                  ? catalog.router_enabled
                    ? "Router picks per turn"
                    : `Uses default (${catalog.default})`
                  : m.is_default
                  ? "Default"
                  : null
              }
              slotProps={{
                primary: { variant: "body2" },
                secondary: { variant: "caption" },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
